import { useState, useRef, useCallback, useEffect } from 'react';
import axiosService from 'utils/axios';

const POLL_INTERVAL_MS = 1000;

interface VehicleExportStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  file: string | null;
  errorMessage: string | null;
}

interface UseVehicleExportReturn {
  isExporting: boolean;
  exportProgress: number;
  exportResult: VehicleExportStatus | null;
  startExport: () => void;
  clearResult: () => void;
}

interface VehicleExportResponse {
  id: string;
  status: VehicleExportStatus['status'];
  progress: number;
  file: string | null;
  error_message: string | null;
}

const mapResponseToExportStatus = (data: VehicleExportResponse): VehicleExportStatus => ({
  id: data.id,
  status: data.status,
  progress: data.progress,
  file: data.file,
  errorMessage: data.error_message,
});

export const useVehicleExport = (): UseVehicleExportReturn => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportResult, setExportResult] = useState<VehicleExportStatus | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const pollExportStatus = useCallback(
    (exportId: string) => {
      pollIntervalRef.current = setInterval(() => {
        axiosService
          .get(`/vehicles/export/${exportId}/`)
          .then(res => {
            const result = mapResponseToExportStatus(res.data);
            setExportProgress(result.progress);

            if (result.status === 'completed' || result.status === 'failed') {
              stopPolling();
              setIsExporting(false);
              setExportResult(result);
            }
          })
          .catch(err => {
            stopPolling();
            setIsExporting(false);
            setExportResult({
              id: exportId,
              status: 'failed',
              progress: 0,
              file: null,
              errorMessage: err.message || 'Failed to check export status',
            });
          });
      }, POLL_INTERVAL_MS);
    },
    [stopPolling]
  );

  const startExport = useCallback(() => {
    setIsExporting(true);
    setExportProgress(0);
    setExportResult(null);

    axiosService
      .post('/vehicles/export/')
      .then(res => {
        const result = mapResponseToExportStatus(res.data);
        if (result.status === 'completed') {
          setIsExporting(false);
          setExportResult(result);
        } else {
          pollExportStatus(result.id);
        }
      })
      .catch(err => {
        setIsExporting(false);
        setExportResult({
          id: '',
          status: 'failed',
          progress: 0,
          file: null,
          errorMessage: err.message || 'Failed to start export',
        });
      });
  }, [pollExportStatus]);

  const clearResult = useCallback(() => {
    setExportResult(null);
    setExportProgress(0);
  }, []);

  return {
    isExporting,
    exportProgress,
    exportResult,
    startExport,
    clearResult,
  };
};
